import React, { useState, useMemo, useEffect, useContext } from 'react';
import MergelyView from 'mergely-react'
import { GlobalContext } from '../GlobalContext';
import NavBar from "../partials/NavBar";

const Differ = () => {
    const { socket, isSocketReady, username, logout, toast, updateDatabase } = useContext(GlobalContext);

    const [id, setId] = useState('');
    const [type, setType] = useState('item');
    const [language, setLanguage] = useState('eu');
    const [level, setLevel] = useState('');
    const [enchant, setEnchant] = useState('0');
    const [lhsContent, setLhsContent] = useState('I like apples!');
    const [rhsContent, setRhsContent] = useState('I like bananas!');
    const [isReady, setIsReady] = useState(false);
    const [enchantedSkillDiffs, setEnchantedSkillDiffs] = useState([]);
    const [selectedEnchantLevel, setSelectedEnchantLevel] = useState(null);

    const memoizedLhsContent = useMemo(() => lhsContent, [lhsContent]);
    const memoizedRhsContent = useMemo(() => rhsContent, [rhsContent]);

    useEffect(() => {
      if (isSocketReady && !isReady) {
        setIsReady(true); // Mark as ready after initial render
      }
      }, [isSocketReady]);

    // Capture CTRL+V and populate the fields from clipboard skill data. Depends on
    // `type` so the parser sees the current selection without imperative DOM reads.
    useEffect(() => {
      const handlePaste = (e) => {
        if (!(e.ctrlKey && e.key === 'v')) return;

        navigator.clipboard.readText().then((text) => {
          // Tab-delimited skill data (only meaningful in skill mode)
          if (text.includes('\t')) {
            if (type !== 'skill') return;
            const lines = text.split('\t');
            if (lines.length === 0 || !lines[0].includes('skill_id=')) return;

            const skillId = lines[0].split('=')[1];
            if (lines.length > 1 && lines[1].includes('skill_level=')) {
              const parsedLevel = lines[1].split('=')[1];
              if (lines.length > 2 && lines[2].includes('skill_sublevel=')) {
                const parsedEnchant = lines[2].split('=')[1];
                setId(skillId);
                setLevel(parsedLevel);
                setEnchant(parsedEnchant > 0 ? parsedEnchant : '0');
              }
            }
          } else if (text.includes('id=')) {
            setId(text.split('=')[1]);
          }
        });
      };

      document.addEventListener('keydown', handlePaste);
      return () => {
        document.removeEventListener('keydown', handlePaste);
      };
    }, [type]);

    useEffect(() => {
      if (!socket || !isSocketReady) {
        return;
      }
      const handleDiffs = (data) => {
        console.log("Received from server:", data);
        let isEnchanted = data.type === 'enchantedSkill';
        if (isEnchanted) {
          handleEnchantedDiffs(data.diffs);
          return;
        }

        // If data contains id, set it to the state
        if (data.id) {
          data.oldDescription = data.oldDescription.replace(/\\n/g, '\n');
          data.newDescription = data.newDescription.replace(/\\n/g, '\n');
          setLhsContent(data.oldDescription);
          setRhsContent(data.newDescription);
          // Clear enchanted skill diffs when showing regular diffs
          setEnchantedSkillDiffs([]);
          setSelectedEnchantLevel(null);
        }
        else
        {
          toast.error("No data found for the given ID.");
        }
      };

      const handleEnchantedDiffs = (data) => {
        console.log("Received enchanted diffs from server:", data);
        if (data.length > 0) {
          setEnchantedSkillDiffs(data);
          // Auto-select the first diff level
          if (data[0]) {
            setSelectedEnchantLevel(data[0].enchantLevel);
            setLhsContent(data[0].oldDescription.replace(/\\n/g, '\n'));
            setRhsContent(data[0].newDescription.replace(/\\n/g, '\n'));
          }
        } else {
          toast.error("No enchanted skill differences found for the given ID.");
          setEnchantedSkillDiffs([]);
          setSelectedEnchantLevel(null);
        }
      };

      // Register the event listeners
      socket.on("diffs", handleDiffs);

      // Cleanup function to remove the listeners
      return () => {
        socket.off("diffs", handleDiffs);
      };
    }, [socket, isSocketReady]);

    const handleEnchantLevelSelect = (enchantLevel) => {
      const selectedDiff = enchantedSkillDiffs.find(diff => diff.enchantLevel === enchantLevel);
      if (selectedDiff) {
        setSelectedEnchantLevel(enchantLevel);
        setLhsContent(selectedDiff.oldDescription.replace(/\\n/g, '\n'));
        setRhsContent(selectedDiff.newDescription.replace(/\\n/g, '\n'));
      }
    };

    const handleFormSubmit = (e) => {
      e.preventDefault();
      const fId = type === 'skill' ? `${id}_${level}_${enchant}` : id;

      socket.emit("getDiffs", {
        type: type,
        lang: language,
        id: fId,
      });

      console.log('Type:', type, 'Language:', language, 'ID:', fId);
    };

    const MemoizedMergelyView = useMemo(() => {
      return (
        <MergelyView
          height="100%"
          width="100%"
          lhs={memoizedLhsContent}
          rhs={memoizedRhsContent}
          wrap_lines={true}
          cmsettings={{ readOnly: true }}
        />
      );
    }, [memoizedLhsContent, memoizedRhsContent]);


    return (
      <div className="App">
        <h1>L2 Localization Tool</h1>
        <div className="search-container">
          <NavBar
            username={username}
            onLogout={logout}
            updateDatabase={updateDatabase}
          />
          <h2>Data Diff Viewer</h2>
          <div className="search-field-container">
        <form onSubmit={handleFormSubmit} className='toggle-section'>
          <label>
            <input
              type="text"
              value={id}
              placeholder='ID'
              onChange={(e) => setId(e.target.value)}
              style={{
                marginLeft: '10px',
                padding: '5px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                width: '100px',
              }}
            />
          </label>
          {type === 'skill' && (
          <div id="toggle-container" style={{display: 'inline-flex'}}>
          <label>
            <input
              type="text"
              placeholder='Level'
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              style={{
                marginLeft: '10px',
                padding: '5px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                width: '100px',
              }}
            />
          </label>
          <label style={{marginLeft: '20px'}}>
            Enchant:
          <select
            value={enchant}
            onChange={(e) => setEnchant(e.target.value)}
            style={{
              padding: '5px',
              borderRadius: '4px',
              border: '1px solid #ccc',
            }}
          >
            <option value="0">0</option>
            <option value="1001">+1</option>
            <option value="1002">+2</option>
            <option value="1003">+3</option>
          </select>
          </label>
          </div>
          )}
          <label>
            Type:
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{
              marginLeft: '10px',
              padding: '5px',
              borderRadius: '4px',
              border: '1px solid #ccc',
            }}
          >
            <option value="item">Item</option>
            <option value="skill">Skill</option>
            <option value="enchantedSkill">Enchanted Item Skill</option>
          </select>
          </label>
          <label>
            Language:
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{
              marginLeft: '10px',
              padding: '5px',
              borderRadius: '4px',
              border: '1px solid #ccc',
            }}
          >
            <option value="eu">English</option>
            <option value="k">Korean</option>
            <option value="k2">Korean2</option>
            <option value="ru">Russian</option>
          </select>
          </label>
          <button
            type="submit"
            style={{
              marginLeft: '10px',
              padding: '5px 15px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#007bff',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Submit
          </button>
        </form>
        </div>

        {/* Enchanted Skill Levels Container */}
        {type === 'enchantedSkill' && enchantedSkillDiffs.length > 0 && (
          <div id="enchanted-levels-container" style={{marginTop: '10px'}}>
            <div>
              <h3>Enchant Levels with Differences:</h3>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                {enchantedSkillDiffs.map((diff) => (
                  <button
                    key={diff.enchantLevel}
                    onClick={() => handleEnchantLevelSelect(diff.enchantLevel)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      backgroundColor: selectedEnchantLevel === diff.enchantLevel ? '#007bff' : '#f8f9fa',
                      color: selectedEnchantLevel === diff.enchantLevel ? '#fff' : '#000',
                      cursor: 'pointer',
                    }}
                  >
                    Enchant +{diff.enchantLevel}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div
        style={{
          width: '100%',
          height: '500px',
          border: '1px solid #ccc',
          marginTop: '20px',
        }}
      >
        {isReady && MemoizedMergelyView}
      </div>
      </div>
      </div>
    );
  };

  export default Differ;
